import { Button, Flex, Form, Modal, type ModalProps, message } from 'antd';
import { FC, useEffect } from 'react';

import { useAdminCompanyController_updateOneById } from '@api-client/generated/AdminCompanyController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { CompanyDetailsWrapperFormControl } from '@entities';
import { useCurrentCompanyId } from '@hooks';
import { captureException } from '@sentry/react';
import { getApiErrorMessage } from '@utils';

import * as S from './styled';

const SENTRY_TAGS = { feature: 'bob-id' } as const;

type FormType = Pick<Schemas.AdminCompanyResponseDto, 'bobReferenceId'>;

type CompanyBobIdEditProps = {
  company: Schemas.AdminCompanyResponseDto;
  onCancel: () => void;
  onAfterEdit: () => void;
} & ModalProps;

/**
 * The BOB50 dossier code, and nothing else.
 *
 * This used to be the whole company form — name, VAT, RCS, addresses, bank details. All of that is
 * client data that master-data-service is taking ownership of, so the backoffice reads it and links
 * out to MDS. The dossier code stays because it is ours: it is the key the BOB50 integration looks
 * a company up by, it exists nowhere in MDS, and a CSM attaching a company to its dossier is not
 * editing client data.
 */
const CompanyBobIdEdit: FC<CompanyBobIdEditProps> = ({
  company,
  open,
  onAfterEdit,
  onCancel,
  ...rest
}) => {
  const companyId = useCurrentCompanyId();

  const [form] = Form.useForm<FormType>();

  const { mutate: updateCompany, isPending: loading } =
    useAdminCompanyController_updateOneById();

  const initialValues: FormType = { bobReferenceId: company.bobReferenceId };

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [company, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = (values: FormType) => {
    const bobReferenceId = values.bobReferenceId?.trim();

    // Nothing typed and nothing stored: the request body would serialise to `{}`, and
    // `companies.update` with no columns only bumps `updated_at` — behind a "successfully updated"
    // toast. (It does not even fail loudly: TypeORM raises `UpdateValuesMissingError` on an empty
    // value set unless the entity has an @UpdateDateColumn, which this one does.)
    if (!bobReferenceId && !company.bobReferenceId) {
      return handleCancel();
    }

    updateCompany(
      {
        parameter: {
          id: companyId!,
        },
        // A partial update — the service passes this straight to `companies.update`, so the columns
        // left out here keep whatever MDS last put in them.
        // The BOB50 integration looks a company up by exact equality on this dossier code, so
        // padding pasted into the field breaks every BOB50 call — hence the trim above.
        requestBody: { bobReferenceId },
      },
      {
        onSuccess: () => {
          message.success('BOB ID has been successfully updated');

          onAfterEdit();
          handleCancel();
        },
        // The axios interceptor only handles a 401, so without this a rejected write left the
        // modal open with no explanation — and the client-facing lock made 403 a real answer here.
        // Captured as well as shown: a toast is gone in four seconds, and a write refused for a
        // reason nobody expected (a 403 naming a field, a 500) has to leave something behind.
        onError: (error) => {
          captureException(error, { level: 'error', tags: SENTRY_TAGS });

          return message.error(
            getApiErrorMessage(error, 'Could not update the BOB ID.')
          );
        },
      }
    );
  };

  return (
    <Modal
      width={575}
      open={open}
      title="Edit BOB ID"
      footer={null}
      onCancel={handleCancel}
      centered
      {...rest}
    >
      <Form<FormType>
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        requiredMark={false}
        initialValues={initialValues}
      >
        <S.Card>
          <CompanyDetailsWrapperFormControl
            type="input"
            form={{
              label: 'BOB50 Dossier',
              name: 'bobReferenceId',
            }}
          />
        </S.Card>

        <S.Submit>
          <Form.Item noStyle>
            <Flex justify="flex-end" gap={12}>
              <Button onClick={handleCancel} size="large">
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
              >
                Save
              </Button>
            </Flex>
          </Form.Item>
        </S.Submit>
      </Form>
    </Modal>
  );
};

export default CompanyBobIdEdit;
